<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import CannedResponseFileField from 'dashboard/components/widgets/conversation/CannedResponseFileField.vue';
import CannedResponseTypeSelector from './CannedResponseTypeSelector.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    CannedResponseFileField,
    CannedResponseTypeSelector,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edfile: { type: Object, default: null },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      replyType: this.edfile?.file_url ? 'image' : 'text',
      content: this.edcontent || '',
      existingFile: this.edfile,
      imageFile: null,
      removeExistingFile: false,
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    displayedExistingFile() {
      return this.removeExistingFile ? null : this.existingFile;
    },
    hasImage() {
      return Boolean(this.imageFile || this.displayedExistingFile?.file_url);
    },
    isSubmitDisabled() {
      const missingBody =
        this.replyType === 'text' ? !this.content?.trim() : !this.hasImage;

      return (
        this.v$.shortCode.$invalid || this.editCanned.showLoading || missingBody
      );
    },
  },
  watch: {
    replyType(newType, oldType) {
      if (newType === oldType) return;
      if (newType === 'text') {
        this.imageFile = null;
        this.removeExistingFile = true;
        this.content = this.content || '';
      } else {
        this.content = '';
        this.removeExistingFile = false;
      }
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.imageFile = null;
      this.v$.shortCode.$reset();
    },
    buildPayload() {
      const payload = {
        id: this.id,
        short_code: this.shortCode,
      };

      if (this.replyType === 'image') {
        payload.content = '';
        if (this.imageFile) payload.file = this.imageFile;
        if (this.removeExistingFile) payload.remove_file = true;
      } else {
        payload.content = this.content;
        if (this.edfile?.file_url) payload.remove_file = true;
      }

      return payload;
    },
    editCannedResponse() {
      this.editCanned.showLoading = true;
      this.$store
        .dispatch('updateCannedResponse', this.buildPayload())
        .then(() => {
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full mt-4">
          <CannedResponseTypeSelector v-model="replyType" />
        </div>

        <div v-if="replyType === 'text'" class="w-full mt-4">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
            />
          </div>
        </div>

        <div v-else class="w-full mt-4">
          <CannedResponseFileField
            :existing-file="displayedExistingFile"
            @update:file="imageFile = $event"
            @update:remove-existing="removeExistingFile = $event"
          />
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="isSubmitDisabled"
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];

  p {
    @apply text-base;
  }
}
</style>
